/**
 * PDP media gallery — looping navigation (Intelligems A/B variant).
 *
 * Ships INERT. With no activation flag present this file registers nothing,
 * patches nothing and leaves the control PDP byte-identical in behaviour.
 *
 * Activation — any one of:
 *   1. CSS custom property (what Intelligems injects in a Style experiment):
 *        :root { --pdp-gallery-loop: 1; }
 *   2. A class on <html> or <body>:  pdp-gallery-loop
 *   3. QA URL parameter:             ?galleryloop=on   (?galleryloop=off forces control)
 *
 * What it changes when active:
 *   - Arrows (thumbnail strip, visible on desktop AND mobile) wrap around
 *     instead of stopping: last -> first, first -> last.
 *   - On mobile, a swipe past either edge of the scroll-snap carousel jumps to
 *     the opposite end.
 *
 * Both paths go through MediaGallery.setActiveMedia(), so is-active, the
 * thumbnail highlight, the live region and media playback all stay in sync.
 *
 * The visible slide set is read live as `:not(.vtf-hidden)` because the Variant
 * Thumbnail Filter (snippets/product-media-gallery.liquid) hides slides that
 * don't belong to the selected variant. The wrap must land on the last slide of
 * the CURRENT variant, not the last <li> in the DOM.
 */
(function () {
  'use strict';

  if (window.__pdpGalleryLoop) return;
  window.__pdpGalleryLoop = { active: false };

  var SWIPE_THRESHOLD = 40; // px of horizontal travel before an edge swipe counts
  var GALLERY_UL = 'ul[id^="Slider-Gallery-"]';

  /* ---------------------------------------------------------------- flags */

  function urlFlag() {
    try {
      var v = new URLSearchParams(location.search).get('galleryloop');
      if (v === null) return null;
      return v !== 'off' && v !== '0' && v !== 'false';
    } catch (e) {
      return null;
    }
  }

  function cssFlag() {
    try {
      var v = getComputedStyle(document.documentElement)
        .getPropertyValue('--pdp-gallery-loop')
        .trim();
      return v === '1' || v === 'on' || v === 'true';
    } catch (e) {
      return false;
    }
  }

  function classFlag() {
    var html = document.documentElement;
    var body = document.body;
    return (
      (html && html.classList.contains('pdp-gallery-loop')) ||
      (body && body.classList.contains('pdp-gallery-loop'))
    );
  }

  function isEnabled() {
    var forced = urlFlag();
    if (forced !== null) return forced; // ?galleryloop=off wins over a live variant
    return cssFlag() || classFlag();
  }

  /* -------------------------------------------------------------- helpers */

  // Slides the shopper can actually reach right now: in the DOM, not filtered
  // out by the variant thumbnail filter, and not collapsed by CSS.
  function visibleSlides(viewer) {
    return Array.from(viewer.querySelectorAll('li[data-media-id]:not(.vtf-hidden)'));
  }

  // Index of the slide the scroll container is currently parked on. Derived
  // from offsets rather than `scrollLeft >= scrollWidth - clientWidth` because
  // the mobile slider carries trailing padding (`.slider--mobile:after`), so
  // the last snap position sits ~55px short of the maximum scroll.
  function scrolledIndex(ul, slides) {
    var origin = slides[0].offsetLeft;
    var left = ul.scrollLeft;
    var best = 0;
    var bestDelta = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var delta = Math.abs(slides[i].offsetLeft - origin - left);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = i;
      }
    }
    return best;
  }

  /* --------------------------------------------------------------- arrows */

  function patchNavigateViewer(Ctor) {
    var proto = Ctor && Ctor.prototype;
    if (!proto || proto.__loopPatched) return;
    proto.__loopPatched = true;

    // Same contract as the stock navigateViewer (media-gallery.js) — only the
    // clamp becomes a modulo. The stock version is kept so the patch can be
    // reasoned about (and reverted) in isolation.
    proto.navigateViewer = function (direction) {
      var slides = visibleSlides(this.elements.viewer);
      if (slides.length < 2) return;

      var active =
        this.elements.viewer.querySelector('li.is-active[data-media-id]:not(.vtf-hidden)') || slides[0];
      var current = slides.indexOf(active);
      if (current === -1) current = 0;

      var next = (current + direction) % slides.length;
      if (next < 0) next += slides.length;
      if (next === current) return;

      this.setActiveMedia(slides[next].dataset.mediaId, false);
    };
  }

  /* ---------------------------------------------------------- mobile swipe */

  var touch = null;

  function onTouchStart(event) {
    touch = null;
    if (!window.__pdpGalleryLoop.active) return;
    if (!event.touches || event.touches.length !== 1) return; // ignore pinch

    var ul = event.target.closest && event.target.closest(GALLERY_UL);
    if (!ul) return;
    if (ul.scrollWidth - ul.clientWidth < 2) return; // desktop: not scrollable

    var slides = visibleSlides(ul);
    if (slides.length < 2) return;

    touch = {
      ul: ul,
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
      index: scrolledIndex(ul, slides),
      count: slides.length,
    };
  }

  function onTouchEnd(event) {
    var start = touch;
    touch = null;
    if (!start || !window.__pdpGalleryLoop.active) return;
    if (!event.changedTouches || !event.changedTouches.length) return;

    var dx = event.changedTouches[0].clientX - start.x;
    var dy = event.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > Math.abs(dx)) return; // not a horizontal swipe

    var slides = visibleSlides(start.ul);
    if (slides.length < 2 || slides.length !== start.count) return; // variant changed mid-swipe

    // Still parked on the same edge the swipe started from? If the carousel
    // moved, the browser handled the swipe and there is nothing to wrap.
    var index = scrolledIndex(start.ul, slides);
    if (index !== start.index) return;

    var target = null;
    if (dx < 0 && index === slides.length - 1) target = slides[0]; // swipe left past the end
    if (dx > 0 && index === 0) target = slides[slides.length - 1]; // swipe right past the start
    if (!target) return;

    var gallery = start.ul.closest('media-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      // setActiveMedia scrolls with behavior:'instant', which is what a wrap
      // should feel like — smooth would whoosh through every slide in between.
      gallery.setActiveMedia(target.dataset.mediaId, false);
    } else {
      start.ul.scrollTo({ left: target.offsetLeft - slides[0].offsetLeft, behavior: 'instant' });
    }
  }

  /* ------------------------------------------------------------- activate */

  function activate() {
    if (window.__pdpGalleryLoop.active) return;
    if (!document.querySelector('media-gallery ' + GALLERY_UL)) return;

    window.__pdpGalleryLoop.active = true;

    var apply = function () {
      patchNavigateViewer(customElements.get('media-gallery'));
    };
    if (customElements.get('media-gallery')) apply();
    else customElements.whenDefined('media-gallery').then(apply);

    // Delegated so the listeners survive section re-renders (variant changes,
    // the theme editor) without needing to be re-attached.
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
  }

  // Intelligems' bundle is async, so its CSS/class can land after this defer
  // script runs. React to the injection itself, and keep a wall-clock backstop —
  // a tick-counted one would be wrong, because browsers throttle setInterval to
  // ~1Hz in a backgrounded tab and the window would stretch by 6x.
  function watch() {
    if (isEnabled()) {
      activate();
      return;
    }

    var deadline = Date.now() + 15000;
    var timer = null;
    var observer = null;

    function stop() {
      if (timer) clearInterval(timer);
      if (observer) observer.disconnect();
    }

    function check() {
      if (isEnabled()) {
        stop();
        activate();
      } else if (Date.now() > deadline) {
        stop();
      }
    }

    if (window.MutationObserver) {
      observer = new MutationObserver(check);
      // A Style experiment appends a <style> to <head>; a JS one flips a class.
      observer.observe(document.head, { childList: true });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
      if (document.body) {
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      }
    }

    timer = setInterval(check, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
